"use strict";

// tsup.inject.js
import * as React from "react";

// src/GoogleMapsProvider.tsx
import * as React2 from "react";
import { useScript } from "@ttoss/hooks";
var GoogleMapsContext = React2.createContext({
  status: "idle",
  googleMaps: null
});
var GoogleMapsProvider = ({
  children,
  apiKey,
  libraries,
  language
}) => {
  const src = (() => {
    let srcTemp = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    if (libraries) {
      srcTemp = srcTemp + `&libraries=${libraries.join(",")}`;
    }
    if (language) {
      srcTemp = srcTemp + `&language=${language}`;
    }
    return srcTemp;
  })();
  const { status } = useScript(src);
  const googleMaps = React2.useMemo(() => {
    if (status === "ready" && window.google.maps) {
      return window.google.maps;
    }
    return null;
  }, [status]);
  const value = React2.useMemo(() => {
    if (status === "ready" && googleMaps) {
      return {
        status,
        googleMaps
      };
    }
    return {
      status,
      googleMaps: null
    };
  }, [googleMaps, status]);
  return /* @__PURE__ */ React2.createElement(GoogleMapsContext.Provider, {
    value
  }, children);
};
var useGoogleMaps = () => React2.useContext(GoogleMapsContext);

// src/useGeocoder.ts
import * as React3 from "react";
var useGeocoder = () => {
  const { googleMaps } = useGoogleMaps();
  const [isGeocoderInitialized, setIsGeocoderInitialized] = React3.useState(false);
  const geocoder = React3.useMemo(() => {
    if (googleMaps) {
      const googleMapsGeocoder = new googleMaps.Geocoder();
      setIsGeocoderInitialized(true);
      return googleMapsGeocoder;
    }
    return null;
  }, [googleMaps]);
  return { geocoder, isGeocoderInitialized };
};

// src/useMap.ts
import * as React4 from "react";
import { useCallbackRef } from "use-callback-ref";
var useMap = (options = {}) => {
  const [, forceUpdate] = React4.useState(0);
  const ref = useCallbackRef(null, () => forceUpdate((n) => n + 1));
  const { googleMaps } = useGoogleMaps();
  const map = React4.useMemo(() => {
    if (googleMaps && ref.current) {
      return new googleMaps.Map(ref.current, options);
    }
    return null;
  }, [googleMaps, ref.current]);
  const optionsStringify = JSON.stringify(options);
  React4.useEffect(() => {
    if (map) {
      const parsedOptions = JSON.parse(optionsStringify);
      map.setOptions(parsedOptions);
    }
  }, [optionsStringify, map]);
  return {
    map,
    ref
  };
};

// src/usePlacesAutocomplete/index.ts
import { useCallback, useEffect as useEffect2, useRef as useRef2, useState as useState3 } from "react";

// src/usePlacesAutocomplete/debounce.ts
var debounce = (fn, delay) => {
  let timer;
  function debounceFn(...args) {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    timer = setTimeout(() => fn.apply(this, args), delay);
  }
  return debounceFn;
};
var debounce_default = debounce;

// src/usePlacesAutocomplete/useLatest.ts
import { useRef } from "react";
var useLatest_default = (val) => {
  const ref = useRef(val);
  ref.current = val;
  return ref;
};

// src/usePlacesAutocomplete/index.ts
var usePlacesAutocomplete = ({
  requestOptions,
  debounce: debounce2 = 200,
  cache = 24 * 60 * 60,
  cacheKey,
  callbackName,
  defaultValue = "",
  initOnMount = true
} = {}) => {
  const [ready, setReady] = useState3(false);
  const [value, setVal] = useState3(defaultValue);
  const [suggestions, setSuggestions] = useState3({
    loading: false,
    status: "",
    data: []
  });
  const asRef = useRef2(null);
  const requestOptionsRef = useLatest_default(requestOptions);
  const { googleMaps } = useGoogleMaps();
  const googleMapsRef = useLatest_default(googleMaps);
  const upaCacheKey = cacheKey ? `upa-${cacheKey}` : "upa";
  const init = useCallback(() => {
    if (asRef.current)
      return;
    if (!googleMaps) {
      return;
    }
    const { current: gMaps } = googleMapsRef;
    const placesLib = (gMaps == null ? void 0 : gMaps.places) || googleMaps.places;
    if (!placesLib) {
      return;
    }
    asRef.current = new placesLib.AutocompleteService();
    setReady(true);
  }, [googleMaps]);
  const clearSuggestions = useCallback(() => {
    setSuggestions({ loading: false, status: "", data: [] });
  }, []);
  const clearCache = useCallback(() => {
    try {
      sessionStorage.removeItem(upaCacheKey);
    } catch (error) {
    }
  }, []);
  const fetchPredictions = useCallback(debounce_default((val) => {
    var _a;
    if (!val) {
      clearSuggestions();
      return;
    }
    setSuggestions((prevState) => ({ ...prevState, loading: true }));
    let cachedData = {};
    try {
      cachedData = JSON.parse(sessionStorage.getItem(upaCacheKey) || "{}");
    } catch (error) {
    }
    if (cache) {
      cachedData = Object.keys(cachedData).reduce((acc, key) => {
        if (cachedData[key].maxAge - Date.now() >= 0)
          acc[key] = cachedData[key];
        return acc;
      }, {});
      if (cachedData[val]) {
        setSuggestions({
          loading: false,
          status: "OK",
          data: cachedData[val].data
        });
        return;
      }
    }
    (_a = asRef == null ? void 0 : asRef.current) == null ? void 0 : _a.getPlacePredictions({ ...requestOptionsRef.current, input: val }, (data, status) => {
      setSuggestions({ loading: false, status, data: data || [] });
      if (cache && status === "OK") {
        cachedData[val] = {
          data,
          maxAge: Date.now() + cache * 1e3
        };
        try {
          sessionStorage.setItem(upaCacheKey, JSON.stringify(cachedData));
        } catch (error) {
        }
      }
    });
  }, debounce2), [debounce2, clearSuggestions]);
  const setValue = useCallback((val, shouldFetchData = true) => {
    setVal(val);
    if (asRef.current && shouldFetchData)
      fetchPredictions(val);
  }, [fetchPredictions]);
  useEffect2(() => {
    if (!initOnMount)
      return () => null;
    if (!googleMapsRef.current && !googleMaps && callbackName) {
      window[callbackName] = init;
    } else {
      init();
    }
    return () => {
      if (window[callbackName])
        delete window[callbackName];
    };
  }, [callbackName, init]);
  return {
    ready,
    value,
    suggestions,
    setValue,
    clearSuggestions,
    clearCache,
    init
  };
};
export {
  GoogleMapsProvider,
  useGeocoder,
  useGoogleMaps,
  useMap,
  usePlacesAutocomplete
};
